/**
 * Project Details Facade (Shared)
 * 
 * Encapsulates project details page specific logic:
 * - Fetching single project
 * - Project conversations
 * - Project attachments
 * - Project updates
 * 
 * @module useProjectDetailsFacade
 * @category Facades/Shared
 */

import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { type ProjectFormData } from '../../../types/project.types';
import type { ShowToastFn } from './types';
import { googleDriveService } from '../../../api/services/googleDrive.service';
import {
  selectCurrentProjectId,
  selectShowProjectDetailsDrawer,
  setCurrentProjectId,
  setShowProjectDetailsDrawer,
  selectProjects,
  selectIsLoadingProjects,
  selectProjectMetaData,
  getProject,
  updateProject,
  getProjectConversations,
  clearConversations,
  selectProjectConversations,
  selectIsFetchingConversations,
  getProjectAttachments,
  uploadProjectFiles,
  deleteProjectAttachment,
  clearAttachments,
  selectProjectAttachments,
  selectIsFetchingAttachments,
  selectIsUploadingFiles,
} from '../../..';
export interface ProjectDetailsFacadeReturn {
  // Current project
  currentProject: any | null;
  isLoading: boolean;
  projectMetaData: any;
  
  // Drawer state
  showProjectDetailsDrawer: boolean;
  setDrawerVisibility: (show: boolean) => void;
  
  // Conversations
  conversations: any[];
  isLoadingConversations: boolean;
  
  // Attachments
  attachments: any[];
  isLoadingAttachments: boolean;
  isUploadingFiles: boolean;
  
  // Actions
  fetchProject: (id: string) => Promise<void>;
  fetchAttachments: () => void;
  updateProject: (data: ProjectFormData) => Promise<void>;
  uploadFiles: (files: File[]) => Promise<void>;
  deleteFile: (fileId: string) => Promise<void>;
  fetchConversations: (mode?: 'uncover' | 'prototyper') => void;
  handleDisconnectGoogleDrive: () => Promise<void>;
}

export function useProjectDetailsFacade(
  projectId: string | undefined,
  onNavigateToLanding: () => void,
  showToast: ShowToastFn,
  mode?: 'uncover' | 'prototyper'
): ProjectDetailsFacadeReturn {
  const dispatch = useAppDispatch();

  // ========== Redux State ==========
  const projects = useAppSelector(selectProjects);
  const isLoading = useAppSelector(selectIsLoadingProjects);
  const projectMetaData = useAppSelector(selectProjectMetaData);
  const showProjectDetailsDrawer = useAppSelector(selectShowProjectDetailsDrawer);
  const conversations = useAppSelector(selectProjectConversations);
  const isLoadingConversations = useAppSelector(selectIsFetchingConversations);
  const attachments = useAppSelector(selectProjectAttachments);
  const isLoadingAttachments = useAppSelector(selectIsFetchingAttachments);
  const isUploadingFiles = useAppSelector(selectIsUploadingFiles);
  const currentProjectId = useAppSelector(selectCurrentProjectId);

  // ========== Local State ==========
  const [currentProject, setCurrentProject] = useState<any>(() =>
    projects.find((p: any) => p.id === projectId || p.project_id === projectId)
  );
  const lastFetchedModeRef = useRef<'uncover' | 'prototyper' | undefined>(undefined);

  // ========== Effects ==========
  // Sync project ID to Redux
  useEffect(() => {
    if (projectId && projectId !== currentProjectId) {
      dispatch(setCurrentProjectId(projectId));
    }
  }, [projectId, currentProjectId, dispatch]);

  // Fetch project details if not in store
  useEffect(() => {
    if (!projectId) {
      onNavigateToLanding();
      return;
    }

    const existingProject = projects.find(
      (p: any) => p.id === projectId || p.project_id === projectId
    );

    if (!existingProject && !isLoading) {
      dispatch(getProject(projectId))
        .unwrap()
        .catch(() => {
          onNavigateToLanding();
        });
    }
  }, [projectId, projects, isLoading, dispatch, onNavigateToLanding]);

  // Redirect if project not found after loading
  useEffect(() => {
    if (!isLoading && projects.length > 0 && !currentProject && projectId) {
      const projectExists = projects.some(
        (p: any) => p.id === projectId || p.project_id === projectId
      );
      if (!projectExists) {
        onNavigateToLanding();
      }
    }
  }, [currentProject, isLoading, projects.length, projectId, onNavigateToLanding]);

  // Update current project when projects change
  useEffect(() => {
    setCurrentProject(() =>
      projects.find((p: any) => p.id === projectId || p.project_id === projectId)
    );
  }, [projectId, projects]);

  // Fetch project conversations and attachments
  useEffect(() => {
    if (projectId) {
     
      dispatch(getProjectAttachments(projectId));
    }
    if (projectId && mode && mode !== lastFetchedModeRef.current) {
      console.log('Fetching conversations with mode:', mode);
      lastFetchedModeRef.current = mode;
      dispatch(getProjectConversations({ projectId, mode }));
    }
    // Cleanup: clear data when leaving the page
    return () => {
      dispatch(clearConversations());
      dispatch(clearAttachments());
    };
  }, [projectId, mode, dispatch]);

  // ========== Actions ==========
  const fetchProject = useCallback(
    async (id: string) => {
      try {
        await dispatch(getProject(id)).unwrap();
      } catch (error) {
        console.error('Failed to fetch project:', error);
        throw error;
      }
    },
    [dispatch]
  );

  const handleUpdateProject = useCallback(
    async (data: ProjectFormData) => {
      if (!projectId) {
        console.error('Missing project ID');
        return;
      }

      try {
        await dispatch(
          updateProject({
            projectId,
            projectData: {
              name: data.name,
              description: data.description,
              main_persona: data.main_persona,
              design_process: data.design_process,
              ideoz_role: data.ideoz_role,
              user_role: data.user_role,
              industry: data.industry,
            },
          })
        ).unwrap();

        showToast({
          message: 'Project context updated successfully',
          severity: 'success',
        });
      } catch (error) {
        console.error('Failed to update project:', error);
      }
    },
    [projectId, dispatch, showToast]
  );

  const handleUploadFiles = useCallback(
    async (files: File[]): Promise<void> => {
      if (!projectId) {
        throw new Error('Missing project ID');
      }

      try {
        const uploadPromises = files.map((file) => {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('projectId', projectId);
          return dispatch(uploadProjectFiles({ projectId, formData })).unwrap();
        });

        await Promise.all(uploadPromises);

        showToast({
          message: `Successfully uploaded ${files.length} ${files.length === 1 ? 'file' : 'files'}`,
          severity: 'success',
        });
        // Analytics: file.uploaded tracked via Redux middleware
      } catch (error) {
        console.error('Failed to upload files:', error);
        showToast({
          message: 'Failed to upload files. Please try again.',
          severity: 'error',
        });
        throw error; // Re-throw to let the dialog know upload failed
      }
    },
    [projectId, dispatch, showToast]
  );
  const handleDisconnectGoogleDrive = useCallback(
    async () => {
      if (!projectId) {
        console.error('Missing project ID');
        return;
      }

      try {
        // Use the dedicated Google Drive service to disconnect
        await googleDriveService.disconnectProjectFolder(projectId);

        // Refetch project to update state with latest data
        await dispatch(getProject(projectId)).unwrap();

        showToast({
          message: ' Drive Folder Disconnected Successfully',
          severity: 'success',
        });
      } catch (error) {
        console.error('Failed to disconnect Google Drive:', error);
        showToast({
          message: 'Failed to disconnect Google Drive folder',
          severity: 'error',
        });
      }
    },
    [projectId, dispatch, showToast]
  );
  const handleDeleteFile = useCallback(
    async (fileId: string) => {
      try {
        await dispatch(deleteProjectAttachment(fileId)).unwrap();

        showToast({
          message: 'File deleted successfully',
          severity: 'success',
        });
      } catch (error) {
        console.error('Failed to delete file:', error);
        showToast({
          message: 'Failed to delete file. Please try again.',
          severity: 'error',
        });
      }
    },
    [dispatch, showToast]
  );

  const setDrawerVisibility = useCallback(
    (show: boolean) => {
      dispatch(setShowProjectDetailsDrawer(show));
    },
    [dispatch]
  );

  const fetchConversations = useCallback(
    (filterMode?: 'uncover' | 'prototyper') => {
      if (projectId) {
        dispatch(getProjectConversations({ projectId, mode: filterMode }));
      }
    },
    [projectId, dispatch]
  );

  const fetchAttachments = useCallback(
    () => {
      if (projectId) {
        dispatch(getProjectAttachments(projectId));
      }
    },
    [projectId, dispatch]
  );

  // ========== Return ==========
  return useMemo(
    () => ({
      currentProject,
      isLoading,
      projectMetaData,
      showProjectDetailsDrawer,
      setDrawerVisibility,
      conversations,
      isLoadingConversations,
      attachments,
      isLoadingAttachments,
      isUploadingFiles,
      fetchProject,
      fetchAttachments,
      updateProject: handleUpdateProject,
      uploadFiles: handleUploadFiles,
      deleteFile: handleDeleteFile,
      fetchConversations,
      handleDisconnectGoogleDrive,
    }),
    [
      currentProject,
      isLoading,
      projectMetaData,
      showProjectDetailsDrawer,
      setDrawerVisibility,
      conversations,
      isLoadingConversations,
      attachments,
      isLoadingAttachments,
      isUploadingFiles,
      fetchProject,
      fetchAttachments,
      handleUpdateProject,
      handleUploadFiles,
      handleDeleteFile,
      fetchConversations,
      handleDisconnectGoogleDrive,
    ]
  );
}
